import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  useTheme,
  FormControlLabel,
  Divider,
  Chip,
  TextField,
  makeStyles,
  createStyles,
} from "@material-ui/core";
import { CustomTheme } from "../../../../../../types/customTheme";
import ButtonCommon from "../../../../../../components/buttons/ButtonCommon";
import AvailabilitySelection from "../../../../../../components/time/AvailabilitySelection";
import SwitchCommon from "../../../../../../components/switches/SwitchCommon";
import {
  combineMinuteOfWeekArrays,
  normalizeTimeSelections,
} from "../../../../../../utils/time-conversions";
import _ from "lodash";
import { DeliveryTpe } from "../../../../../../utils/enum";
import { Autocomplete } from "@material-ui/lab";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    autocomplete: {
      marginTop: "4px",
      "& .MuiAutocomplete-input": {
        color: theme.palette.custom.orange.contrastText,
      },
      "& .MuiInputBase-root": {
        color: "inherit",
        backgroundColor: theme.palette.background.entity_background,
        borderRadius: 10,
      },
      [`& fieldset`]: {
        borderRadius: 10,
        border: `1px solid ${theme.palette.background.entity_border}`,
        color: theme.palette.custom.orange.contrastText,
      },
    },
    titleColor: {
      color: theme.palette.custom.orange.contrastText,
    },
  }),
);

// Platform selector object
export const platformSelectorObj: any = {
  "walk in": { id: "walk in", label: "Walk In" },
  phone: { id: "phone", label: "Phone" },
  kiosk: { id: "kiosk", label: "Kiosk" },
  online: { id: "online", label: "Online" },
};

// Channel selector list
export const channelSelectorList = [
  { id: "eatpresto", label: "Eat Presto" },
  { id: "deliverect", label: "Deliverect" },
  { id: "justeat", label: "Just Eat" },
  { id: "deliveroo", label: "Deliveroo" },
  { id: "ubereats", label: "Uber Eats" },
];

interface PlatformOption {
  id: string;
  label: string;
}

interface ChannelOption {
  id: string;
  label: string;
}

interface AvailabilitySectionsProps {
  menuOptionNodeObj: any;
  menuOptionData?: any;
  onDataUpdate?: (data: any) => void;
  // Props from parent for controlled mode
  isActive?: boolean;
  setIsActive?: (value: boolean) => void;
  deliveryTypesList?: any[];
  setDeliveryTypesList?: (value: any[]) => void;
  platformList?: any[];
  setPlatformList?: (value: any[]) => void;
  channelList?: any[];
  setChannelList?: (value: any[]) => void;
  availabilityList?: any;
  setAvailabilityList?: (value: any) => void;
  isActiveInitial?: boolean;
  setIsActiveInitial?: (value: boolean) => void;
  deliveryTypesListInitial?: any[];
  setDeliveryTypesListInitial?: (value: any[]) => void;
  platformListInitial?: any[];
  setPlatformListInitial?: (value: any[]) => void;
  channelListInitial?: any[];
  setChannelListInitial?: (value: any[]) => void;
  availabilityListInitial?: any;
  setAvailabilityListInitial?: (value: any) => void;
  originalNodeData?: any;
  setOriginalNodeData?: (value: any) => void;
}

const AvailabilitySections: React.FC<AvailabilitySectionsProps> = ({
  menuOptionNodeObj,
  menuOptionData,
  onDataUpdate,
  // Controlled props from parent
  isActive: propIsActive,
  setIsActive: propSetIsActive,
  deliveryTypesList: propDeliveryTypesList,
  setDeliveryTypesList: propSetDeliveryTypesList,
  platformList: propPlatformList,
  setPlatformList: propSetPlatformList,
  channelList: propChannelList,
  setChannelList: propSetChannelList,
  availabilityList: propAvailabilityList,
  setAvailabilityList: propSetAvailabilityList,
  isActiveInitial: propIsActiveInitial,
  setIsActiveInitial: propSetIsActiveInitial,
  deliveryTypesListInitial: propDeliveryTypesListInitial,
  setDeliveryTypesListInitial: propSetDeliveryTypesListInitial,
  platformListInitial: propPlatformListInitial,
  setPlatformListInitial: propSetPlatformListInitial,
  channelListInitial: propChannelListInitial,
  setChannelListInitial: propSetChannelListInitial,
  availabilityListInitial: propAvailabilityListInitial,
  setAvailabilityListInitial: propSetAvailabilityListInitial,
  originalNodeData: propOriginalNodeData,
  setOriginalNodeData: propSetOriginalNodeData,
}) => {
  const theme: CustomTheme = useTheme();

  // Get all platform IDs
  const allPlatformIds = Object.values(platformSelectorObj).map(
    (p: any) => p.id,
  );
  const allChannelIds = channelSelectorList.map((c) => c.id);
  const allDeliveryTypes = [
    DeliveryTpe.DINEIN,
    DeliveryTpe.DELIVERY,
    DeliveryTpe.TAKEOUT,
  ];

  // ✅ Check if delivery types data exists and has values
  const hasDeliveryData = () => {
    if (!menuOptionNodeObj) return false;
    if ("deliveryTypes" in menuOptionNodeObj) {
      const deliveryData = menuOptionNodeObj.deliveryTypes;
      return Array.isArray(deliveryData) && deliveryData.length > 0;
    }
    return false;
  };

  // ✅ Check if platform data exists and has values
  const hasPlatformData = () => {
    if (!menuOptionNodeObj) return false;
    if ("platform" in menuOptionNodeObj) {
      const platformData = menuOptionNodeObj.platform;
      return Array.isArray(platformData) && platformData.length > 0;
    }
    return false;
  };

  // ✅ Check if channel data exists and has values
  const hasChannelData = () => {
    if (!menuOptionNodeObj) return false;
    if ("orderChannel" in menuOptionNodeObj) {
      const channelData = menuOptionNodeObj.orderChannel;
      return Array.isArray(channelData) && channelData.length > 0;
    }
    return false;
  };

  // ✅ Function to determine if delivery types should show all selected
  const shouldShowAllDeliveryTypes = () => {
    // If deliveryTypesList is empty AND no delivery data exists, show all
    if (deliveryTypesList.length === 0 && !hasDeliveryData()) {
      return true;
    }
    // If deliveryTypesList is empty but deliveryTypesListInitial has data, don't show all
    if (deliveryTypesList.length === 0 && deliveryTypesListInitial.length > 0) {
      return false;
    }
    return false;
  };

  // ✅ Function to determine if platforms should show all selected
  const shouldShowAllPlatforms = () => {
    if (platformList.length === 0 && !hasPlatformData()) {
      return true;
    }
    if (platformList.length === 0 && platformListInitial.length > 0) {
      return false;
    }
    return false;
  };

  // ✅ Function to determine if channels should show all selected
  const shouldShowAllChannels = () => {
    if (channelList.length === 0 && !hasChannelData()) {
      return true;
    }
    if (channelList.length === 0 && channelListInitial.length > 0) {
      return false;
    }
    return false;
  };

  // ✅ Get delivery types list (for switches)
  const getDeliveryTypesList = (): string[] => {
    // If no delivery types selected and should show all, return all delivery types
    if (deliveryTypesList.length === 0 && shouldShowAllDeliveryTypes()) {
      return allDeliveryTypes;
    }
    return deliveryTypesList;
  };

  // Convert platform list to objects with label
  const getPlatformObjects = (ids: string[]): PlatformOption[] => {
    if (ids.length === 0 && shouldShowAllPlatforms()) {
      return Object.values(platformSelectorObj);
    }
    return ids.map((id) => ({
      id,
      label: platformSelectorObj[id]?.label || id,
    }));
  };

  // Convert channel list to objects with label
  const getChannelObjects = (ids: string[]): ChannelOption[] => {
    if (ids.length === 0 && shouldShowAllChannels()) {
      return channelSelectorList;
    }
    return ids.map((id) => ({
      id,
      label: channelSelectorList.find((c) => c.id === id)?.label || id,
    }));
  };

  // Local state (used when not controlled)
  const [localDeliveryTypesList, setLocalDeliveryTypesList] = useState<any>([]);
  const [localPlatformList, setLocalPlatformList] = useState<any>([]);
  const [localChannelList, setLocalChannelList] = useState<any>([]);
  const [localAvailabilityList, setLocalAvailabilityList] = useState<any>([]);
  const [localIsActive, setLocalIsActive] = useState(true);
  const [localDeliveryTypesListInitial, setLocalDeliveryTypesListInitial] =
    useState<any>([]);
  const [localPlatformListInitial, setLocalPlatformListInitial] = useState<any>(
    [],
  );
  const [localChannelListInitial, setLocalChannelListInitial] = useState<any>(
    [],
  );
  const [localAvailabilityListInitial, setLocalAvailabilityListInitial] =
    useState<any>([]);
  const [localIsActiveInitial, setLocalIsActiveInitial] = useState(true);
  const [localOriginalNodeData, setLocalOriginalNodeData] = useState<any>({
    entityId: "",
    isActive: false,
    availability: [],
    deliveryTypes: [],
  });
  const [availabilityErrorMessage, setAvailabilityErrorMessage] = useState("");

  // Determine if using controlled or uncontrolled mode
  const isControlled =
    propIsActive !== undefined && propSetIsActive !== undefined;

  // Use props if provided, otherwise use local state
  const deliveryTypesList = isControlled
    ? propDeliveryTypesList
    : localDeliveryTypesList;
  const platformList = isControlled ? propPlatformList : localPlatformList;
  const channelList = isControlled ? propChannelList : localChannelList;
  const availabilityList = isControlled
    ? propAvailabilityList
    : localAvailabilityList;
  const isActive = isControlled ? propIsActive : localIsActive;
  const deliveryTypesListInitial = isControlled
    ? propDeliveryTypesListInitial
    : localDeliveryTypesListInitial;
  const platformListInitial = isControlled
    ? propPlatformListInitial
    : localPlatformListInitial;
  const channelListInitial = isControlled
    ? propChannelListInitial
    : localChannelListInitial;
  const availabilityListInitial = isControlled
    ? propAvailabilityListInitial
    : localAvailabilityListInitial;
  const isActiveInitial = isControlled
    ? propIsActiveInitial
    : localIsActiveInitial;
  const originalNodeData = isControlled
    ? propOriginalNodeData
    : localOriginalNodeData;

  // Setter functions - always use local setters, but update props if controlled
  const setDeliveryTypesList = (value: any[]) => {
    if (isControlled && propSetDeliveryTypesList) {
      propSetDeliveryTypesList(value);
    } else {
      setLocalDeliveryTypesList(value);
    }
  };

  const setPlatformList = (value: any[]) => {
    if (isControlled && propSetPlatformList) {
      propSetPlatformList(value);
    } else {
      setLocalPlatformList(value);
    }
  };

  const setChannelList = (value: any[]) => {
    if (isControlled && propSetChannelList) {
      propSetChannelList(value);
    } else {
      setLocalChannelList(value);
    }
  };

  const setAvailabilityList = (value: any) => {
    if (isControlled && propSetAvailabilityList) {
      propSetAvailabilityList(value);
    } else {
      setLocalAvailabilityList(value);
    }
  };

  const setIsActive = (value: boolean) => {
    if (isControlled && propSetIsActive) {
      propSetIsActive(value);
    } else {
      setLocalIsActive(value);
    }
  };

  const setDeliveryTypesListInitial = (value: any[]) => {
    if (isControlled && propSetDeliveryTypesListInitial) {
      propSetDeliveryTypesListInitial(value);
    } else {
      setLocalDeliveryTypesListInitial(value);
    }
  };

  const setPlatformListInitial = (value: any[]) => {
    if (isControlled && propSetPlatformListInitial) {
      propSetPlatformListInitial(value);
    } else {
      setLocalPlatformListInitial(value);
    }
  };

  const setChannelListInitial = (value: any[]) => {
    if (isControlled && propSetChannelListInitial) {
      propSetChannelListInitial(value);
    } else {
      setLocalChannelListInitial(value);
    }
  };

  const setAvailabilityListInitial = (value: any) => {
    if (isControlled && propSetAvailabilityListInitial) {
      propSetAvailabilityListInitial(value);
    } else {
      setLocalAvailabilityListInitial(value);
    }
  };

  const setIsActiveInitial = (value: boolean) => {
    if (isControlled && propSetIsActiveInitial) {
      propSetIsActiveInitial(value);
    } else {
      setLocalIsActiveInitial(value);
    }
  };

  const setOriginalNodeData = (value: any) => {
    if (isControlled && propSetOriginalNodeData) {
      propSetOriginalNodeData(value);
    } else {
      setLocalOriginalNodeData(value);
    }
  };

  // Initialize states from menuOptionNodeObj
  useEffect(() => {
    if (menuOptionNodeObj && Object.keys(menuOptionNodeObj).length > 0) {
      const deliveryTypes = menuOptionNodeObj.deliveryTypes || [];
      setDeliveryTypesList(deliveryTypes);
      setDeliveryTypesListInitial(deliveryTypes);

      const platform = menuOptionNodeObj.platform || [];
      setPlatformList(platform);
      setPlatformListInitial(platform);

      const orderChannel = menuOptionNodeObj.orderChannel || [];
      setChannelList(orderChannel);
      setChannelListInitial(orderChannel);

      setAvailabilityList(menuOptionNodeObj);
      setAvailabilityListInitial(menuOptionNodeObj);

      const isActive =
        menuOptionNodeObj.isActive !== undefined
          ? menuOptionNodeObj.isActive
          : true;
      setIsActive(isActive);
      setIsActiveInitial(isActive);

      const originalData: any = {
        entityId: menuOptionNodeObj.entityId || "",
        isActive: isActive,
        availability: menuOptionNodeObj.availability || [],
        deliveryTypes: deliveryTypes,
      };

      if ("platform" in menuOptionNodeObj) {
        originalData.platform = menuOptionNodeObj.platform || [];
      }
      if ("orderChannel" in menuOptionNodeObj) {
        originalData.orderChannel = menuOptionNodeObj.orderChannel || [];
      }

      setOriginalNodeData(originalData);
    }
  }, [menuOptionNodeObj]);

  /* Active switch status change using switch */
  const handleSwitchActivate = (e: any) => {
    const { checked } = e.target;
    setIsActive(checked);
  };

  /* Update the deliveryTypeList after selecting the delivery options using switches */
  const handleSelectDeliveryTypes = (e: any) => {
    const { value, name, checked } = e.target;
    if (checked) {
      const selectedList: any = new Set([...deliveryTypesList, value]);
      setDeliveryTypesList([...selectedList]);
    } else {
      const selectedList: any = deliveryTypesList.filter(
        (amountType: string) => name !== amountType,
      );
      setDeliveryTypesList(selectedList);
    }
  };

  /* Update the platform list after selecting the platform options using Autocomplete */
  const handlePlatformChange = (
    event: React.ChangeEvent<{}>,
    selectedOptions: PlatformOption[],
  ) => {
    const uniqueSelected = Array.from(
      new Map(selectedOptions.map((opt) => [opt.id, opt])).values(),
    );
    const selectedIds = uniqueSelected.map((option) => option.id);
    setPlatformList(selectedIds);
  };

  /* Update the channel list after selecting the channel options using Autocomplete */
  const handleChannelChange = (
    event: React.ChangeEvent<{}>,
    selectedOptions: ChannelOption[],
  ) => {
    const uniqueSelected = Array.from(
      new Map(selectedOptions.map((opt) => [opt.id, opt])).values(),
    );
    const selectedIds = uniqueSelected.map((option) => option.id);
    setChannelList(selectedIds);
  };

  /* Entering availability data into a state.*/
  const handleSubmitAvailability = async (
    availabilitySelectionList: Array<any>,
  ) => {
    const combinedMowArrays: any = combineMinuteOfWeekArrays(
      availabilitySelectionList,
    );
    const normalizedArray = _.cloneDeep(combinedMowArrays);

    Object.keys(combinedMowArrays).forEach((day) => {
      normalizedArray[day] = normalizeTimeSelections(normalizedArray[day]);
    });
    const nowArray: any = Object.values(normalizedArray).flat();
    const data = _.cloneDeep(originalNodeData);

    data.availability = nowArray;
    setAvailabilityList(data);
  };

  /* Check if anything has changed */
  const handleDisable = () => {
    // Get the effective delivery types list (considering "show all" logic)
    const effectiveDeliveryTypes = getDeliveryTypesList();

    const isDeliveryEqual = _.isEqual(
      effectiveDeliveryTypes,
      deliveryTypesListInitial,
    );
    const isPlatformEqual = _.isEqual(platformList, platformListInitial);
    const isChannelEqual = _.isEqual(channelList, channelListInitial);
    const isAvailabilityEqual = _.isEqual(
      availabilityList,
      availabilityListInitial,
    );

    if (
      isActive === isActiveInitial &&
      isDeliveryEqual &&
      isPlatformEqual &&
      isChannelEqual &&
      isAvailabilityEqual
    ) {
      return true;
    } else {
      return false;
    }
  };

  // Check if a delivery type is selected (using effective list)
  const isDeliveryTypeSelected = (type: string) => {
    const effectiveDeliveryTypes = getDeliveryTypesList();
    return effectiveDeliveryTypes.includes(type);
  };

  // Get platform objects for Autocomplete
  const platformOptions: PlatformOption[] = Object.values(platformSelectorObj);
  const selectedPlatforms: PlatformOption[] = getPlatformObjects(platformList);

  // Get channel objects for Autocomplete
  const channelOptions: ChannelOption[] = channelSelectorList;
  const selectedChannels: ChannelOption[] = getChannelObjects(channelList);
  const classes = useStyles();

  return (
    <>
      <Paper
        elevation={0}
        style={{
          marginBottom: 24,
          borderRadius: 16,
          backgroundColor: theme.palette.background.menuProductColor,
          padding: 24,
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <Grid container spacing={3}>
          {/* Row 1: Delivery Types */}
          <Grid item xs={12}>
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              style={{ gap: "1px" }}
            >
              <FormControlLabel
                style={{ marginLeft: "0px", marginRight: "8px" }}
                value={DeliveryTpe.DINEIN}
                control={<SwitchCommon />}
                label={
                  <Typography variant="body2" style={{ fontSize: "14px" }}>
                    Dine In
                  </Typography>
                }
                name={DeliveryTpe.DINEIN}
                checked={isDeliveryTypeSelected(DeliveryTpe.DINEIN)}
                labelPlacement="end"
                onChange={handleSelectDeliveryTypes}
              />
              <FormControlLabel
                style={{ marginLeft: "0px", marginRight: "8px" }}
                value={DeliveryTpe.DELIVERY}
                control={<SwitchCommon />}
                label={
                  <Typography variant="body2" style={{ fontSize: "14px" }}>
                    Delivery
                  </Typography>
                }
                name={DeliveryTpe.DELIVERY}
                checked={isDeliveryTypeSelected(DeliveryTpe.DELIVERY)}
                labelPlacement="end"
                onChange={handleSelectDeliveryTypes}
              />
              <FormControlLabel
                style={{ marginLeft: "0px", marginRight: "8px" }}
                value={DeliveryTpe.TAKEOUT}
                name={DeliveryTpe.TAKEOUT}
                control={<SwitchCommon />}
                label={
                  <Typography variant="body2" style={{ fontSize: "14px" }}>
                    Takeout
                  </Typography>
                }
                checked={isDeliveryTypeSelected(DeliveryTpe.TAKEOUT)}
                labelPlacement="end"
                onChange={handleSelectDeliveryTypes}
              />
              <Box flex={1} />
              <FormControlLabel
                style={{ marginLeft: "0px", marginRight: "0px" }}
                control={<SwitchCommon />}
                checked={isActive}
                onChange={handleSwitchActivate}
                label={
                  <Typography variant="body2" style={{ fontSize: "14px" }}>
                    Active
                  </Typography>
                }
                labelPlacement="end"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider style={{ backgroundColor: "rgba(255,255,255,0.08)" }} />
          </Grid>

          {/* Row 2: Platforms - Autocomplete with Chips */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Platforms
            </Typography>
            <Autocomplete
              multiple
              id="platform-selector"
              options={platformOptions}
              className={classes.autocomplete}
              value={selectedPlatforms}
              getOptionLabel={(option: PlatformOption) => option.label}
              onChange={handlePlatformChange}
              renderTags={(value: PlatformOption[], getTagProps) =>
                value.map((option: PlatformOption, index: number) => (
                  <Chip
                    key={option.id}
                    variant="outlined"
                    label={option.label}
                    style={{
                      backgroundColor:
                        theme.palette.background.entity_highlight_background,
                      border: `1px solid ${theme.palette.background.entity_border}`,
                      margin: 2,
                    }}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder="" />
              )}
            />
          </Grid>

          {/* Row 3: Channels - Autocomplete with Chips */}
          <Grid item xs={6}>
            <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
              Order Channels
            </Typography>
            <Autocomplete
              multiple
              id="channel-selector"
              options={channelOptions}
              className={classes.autocomplete}
              value={selectedChannels}
              getOptionLabel={(option: ChannelOption) => option.label}
              onChange={handleChannelChange}
              renderTags={(value: ChannelOption[], getTagProps) =>
                value.map((option: ChannelOption, index: number) => (
                  <Chip
                    key={option.id}
                    variant="outlined"
                    label={option.label}
                    style={{
                      backgroundColor:
                        theme.palette.background.entity_highlight_background,
                      border: `1px solid ${theme.palette.background.entity_border}`,
                      margin: 2,
                    }}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} variant="outlined" placeholder="" />
              )}
            />
          </Grid>
          {/* Row 4: Availability Selection */}
          <Grid item xs={12}>
            <AvailabilitySelection
              nodeData={availabilityList}
              handleSubmit={handleSubmitAvailability}
              setAvailabilityErrorMessage={setAvailabilityErrorMessage}
            />
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

export default AvailabilitySections;
